/* Zora Professional Portal
   Doctor records come from a Shopify metaobject, so adding a provider is an Admin task.
   Checkout is entered through the Cart AJAX API so the fulfillment choice is carried as
   both a cart attribute and a line item property, which is what a delivery-customization
   Function can read. The native doctor discount is applied with /discount/{CODE}.
   Ship-to-customer: confirmation modal only — Shopify Checkout collects the address. */

class ZoraProfessionalPortal extends HTMLElement {
  connectedCallback() {
    const root = this.closest('.zpp') || document;
    this.doctors = this.readJson(root.querySelector('[data-zpp-doctors]')) || [];
    this.config = this.readJson(root.querySelector('[data-zpp-config]')) || {};

    this.index = new Map();
    this.doctors.forEach((doc) => {
      const key = this.normalise(doc.code);
      if (key) this.index.set(key, doc);
    });

    this.current = null;
    this.submitting = false;
    this.shipConfirmed = false;

    this.bind();
  }

  readJson(node) {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  normalise(value) {
    return String(value || '').trim().toUpperCase();
  }

  bind() {
    this.codeInput = this.querySelector('[data-code-input]');
    this.codeError = this.querySelector('[data-code-error]');
    this.formError = this.querySelector('[data-form-error]');
    this.shipNote = this.querySelector('[data-ship-note]');
    this.shipModal = this.querySelector('[data-ship-modal]');
    this.checkoutBtn = this.querySelector('[data-checkout]');
    this.checkoutLabel = this.querySelector('[data-checkout-label]');

    this.querySelector('[data-continue]').addEventListener('click', () => this.lookup());
    this.codeInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.lookup();
      }
    });
    this.codeInput.addEventListener('input', () => this.hide(this.codeError));

    this.querySelectorAll('[data-start-over]').forEach((btn) => {
      btn.addEventListener('click', () => this.reset());
    });

    this.querySelectorAll('[data-fulfilment]').forEach((radio) => {
      radio.addEventListener('change', () => this.onFulfilmentChange(radio));
    });

    this.querySelector('[data-modal-confirm]').addEventListener('click', () => {
      this.shipConfirmed = true;
      this.hide(this.shipModal);
      this.show(this.shipNote);
    });

    this.querySelector('[data-modal-cancel]').addEventListener('click', () => {
      this.shipConfirmed = false;
      this.hide(this.shipModal);
      this.hide(this.shipNote);
      const clinic = this.querySelector('[data-fulfilment][value="clinic"]');
      clinic.checked = true;
    });

    this.checkoutBtn.addEventListener('click', () => this.checkout());
  }

  show(node) {
    if (node) node.hidden = false;
  }

  hide(node) {
    if (node) node.hidden = true;
  }

  onFulfilmentChange(radio) {
    if (!radio.checked) return;
    if (radio.value === 'ship') {
      this.shipConfirmed = false;
      this.hide(this.shipNote);
      this.show(this.shipModal);
    } else {
      this.shipConfirmed = false;
      this.hide(this.shipNote);
    }
  }

  lookup() {
    const code = this.normalise(this.codeInput.value);
    const doctor = this.index.get(code);

    if (!doctor) {
      this.show(this.codeError);
      this.codeInput.focus();
      return;
    }

    this.hide(this.codeError);
    this.current = doctor;

    this.querySelector('[data-doctor-name]').textContent = doctor.doctorName || '—';
    this.querySelector('[data-clinic-name]').textContent = doctor.clinicName || '—';
    this.querySelector('[data-clinic-address]').textContent = this.flatten(doctor.clinicAddress);
    this.querySelector('[data-doctor-code]').textContent = this.normalise(doctor.code);

    this.goToStep(2);
  }

  goToStep(step) {
    this.querySelectorAll('[data-step]').forEach((node) => {
      node.dataset.active = node.dataset.step === String(step) ? 'true' : 'false';
    });
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  reset() {
    this.current = null;
    this.shipConfirmed = false;
    this.codeInput.value = '';
    this.hide(this.codeError);
    this.hide(this.formError);
    this.hide(this.shipNote);
    this.hide(this.shipModal);

    ['[data-first-name]', '[data-last-name]'].forEach((selector) => {
      const field = this.querySelector(selector);
      if (field) field.value = '';
    });

    const checkedSize = this.querySelector('[name="zpp-size"]:checked');
    if (checkedSize) checkedSize.checked = false;

    this.querySelector('[data-fulfilment][value="clinic"]').checked = true;
    this.goToStep(1);
  }

  flatten(value) {
    return String(value || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .join(', ');
  }

  /* Addresses are stored as free text and vary between three and four comma parts,
     so read them from the end: "<street…>, <city>, <STATE> <ZIP>". */
  parseAddress(value) {
    const parts = this.flatten(value).split(',').map((part) => part.trim()).filter(Boolean);
    if (parts.length < 2) return { address1: this.flatten(value), city: '', province: '', zip: '' };

    const tail = parts[parts.length - 1];
    const city = parts[parts.length - 2] || '';
    const address1 = parts.slice(0, -2).join(', ');
    const match = tail.match(/^([A-Za-z .]+?)\s+([\d-]+)$/);

    return {
      address1: address1 || city,
      city,
      province: match ? match[1].trim() : tail,
      zip: match ? match[2].trim() : ''
    };
  }

  fail(message) {
    this.formError.textContent = message;
    this.show(this.formError);
    this.formError.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  collect() {
    const firstName = this.querySelector('[data-first-name]').value.trim();
    const lastName = this.querySelector('[data-last-name]').value.trim();
    const size = this.querySelector('[name="zpp-size"]:checked');
    const fulfilment = this.querySelector('[data-fulfilment]:checked').value;

    if (!firstName || !lastName) {
      this.fail('Enter the patient’s first and last name.');
      return null;
    }
    if (!size) {
      this.fail('Choose a size.');
      return null;
    }
    if (fulfilment === 'ship' && !this.shipConfirmed) {
      this.fail('Confirm ship-to-customer, or choose Fulfilled in Clinic.');
      this.show(this.shipModal);
      return null;
    }

    let address = null;
    if (fulfilment === 'clinic') {
      address = this.parseAddress(this.current.clinicAddress);
    }

    return { firstName, lastName, fulfilment, address, variantId: size.value };
  }

  async post(url, payload) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      const detail = await response.json().catch(() => ({}));
      throw new Error(detail.description || detail.message || `Request to ${url} failed.`);
    }
    return response.json();
  }

  async checkout() {
    if (this.submitting || !this.current) return;

    this.hide(this.formError);
    const data = this.collect();
    if (!data) return;

    const doctorCode = this.normalise(this.current.code);
    const discountCode = this.normalise(this.current.discountCode) || doctorCode;

    this.submitting = true;
    this.checkoutBtn.disabled = true;
    this.checkoutLabel.textContent = 'Preparing checkout…';

    try {
      // Start clean so checkout cannot inherit a stale line or shipping selection.
      await this.post('/cart/clear.js', {});

      await this.post('/cart/add.js', {
        items: [
          {
            id: Number(data.variantId),
            quantity: 1,
            properties: {
              _fulfillment: data.fulfilment,
              _doctor_code: doctorCode,
              Provider: this.current.doctorName || '',
              Clinic: this.current.clinicName || '',
              Patient: `${data.firstName} ${data.lastName}`
            }
          }
        ]
      });

      await this.post('/cart/update.js', {
        attributes: {
          fulfillment_method: data.fulfilment,
          doctor_code: doctorCode,
          doctor_name: this.current.doctorName || '',
          clinic_name: this.current.clinicName || '',
          clinic_address: this.flatten(this.current.clinicAddress),
          patient_name: `${data.firstName} ${data.lastName}`
        }
      });

      window.location.href = this.buildCheckoutUrl(discountCode, data);
    } catch (error) {
      this.submitting = false;
      this.checkoutBtn.disabled = false;
      this.checkoutLabel.textContent = 'Proceed to checkout';
      this.fail(error.message || 'Could not prepare checkout. Please try again.');
    }
  }

  buildCheckoutUrl(discountCode, data) {
    const params = new URLSearchParams();
    params.set('discount', discountCode);
    params.set('checkout[shipping_address][first_name]', data.firstName);
    params.set('checkout[shipping_address][last_name]', data.lastName);
    params.set('checkout[shipping_address][country]', 'US');

    if (data.fulfilment === 'clinic' && data.address) {
      params.set('checkout[shipping_address][address1]', data.address.address1);
      params.set('checkout[shipping_address][city]', data.address.city);
      params.set('checkout[shipping_address][province]', data.address.province);
      params.set('checkout[shipping_address][zip]', data.address.zip);
    }

    return `${this.config.checkoutUrl || '/checkout'}?${params.toString()}`;
  }
}

if (!customElements.get('zora-professional-portal')) {
  customElements.define('zora-professional-portal', ZoraProfessionalPortal);
}
